import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import './BooksPage.css';

const BooksPage = () => {
  const { addToCart } = useCart();
  const [books, setBooks] = useState([]);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const booksPerPage = 12;

  // Sample data - في التطبيق الحقيقي سيأتي من API
  useEffect(() => {
    const sampleBooks = Array.from({ length: 150 }, (_, index) => ({
      id: index + 1,
      title: `كتاب رقم ${index + 1}`,
      author: `المؤلف ${Math.floor(Math.random() * 20) + 1}`,
      price: Math.floor(Math.random() * 100) + 20,
      category: ['شعر', 'قصص', 'تطوير ذات', 'أدب', 'رواية'][Math.floor(Math.random() * 5)],
      description: `وصف مختصر للكتاب رقم ${index + 1} الذي يحتوي على محتوى رائع ومفيد`,
      publishDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1),
      rating: (Math.random() * 2 + 3).toFixed(1),
      pages: Math.floor(Math.random() * 300) + 100,
      language: 'العربية',
      image: '/api/placeholder/200/250'
    }));
    setBooks(sampleBooks);
    setFilteredBooks(sampleBooks);
  }, []);

  // Filter and search logic
  useEffect(() => {
    let filtered = books;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(book => 
        book.title.includes(searchTerm) || 
        book.author.includes(searchTerm) ||
        book.description.includes(searchTerm)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(book => book.category === selectedCategory);
    }

    // Sort
    filtered = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.publishDate) - new Date(a.publishDate);
        case 'oldest':
          return new Date(a.publishDate) - new Date(b.publishDate);
        case 'title':
          return a.title.localeCompare(b.title);
        case 'author':
          return a.author.localeCompare(b.author);
        case 'rating':
          return parseFloat(b.rating) - parseFloat(a.rating);
        default:
          return 0;
      }
    });

    setFilteredBooks(filtered);
    setCurrentPage(1);
  }, [books, searchTerm, selectedCategory, sortBy]);

  // Pagination
  const indexOfLastBook = currentPage * booksPerPage;
  const indexOfFirstBook = indexOfLastBook - booksPerPage;
  const currentBooks = filteredBooks.slice(indexOfFirstBook, indexOfLastBook);
  const totalPages = Math.ceil(filteredBooks.length / booksPerPage);

  const categories = ['all', 'شعر', 'قصص', 'تطوير ذات', 'أدب', 'رواية'];

  return (
    <div className="books-page">
      <div className="page-header">
        <div className="container">
          <h1>مكتبة الكتب</h1>
          <p>اكتشف مجموعة واسعة من الكتب والإصدارات الجديدة</p>
        </div>
      </div>

      <div className="books-content">
        <div className="container">
          {/* Filters and Search */}
          <div className="books-filters">
            <div className="search-box">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="ابحث عن كتاب أو مؤلف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-controls">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-filter"
              >
                <option value="all">جميع الفئات</option>
                {categories.slice(1).map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>

              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-filter"
              >
                <option value="newest">الأحدث</option>
                <option value="oldest">الأقدم</option>
                <option value="title">حسب العنوان</option>
                <option value="author">حسب المؤلف</option>
                <option value="rating">حسب التقييم</option>
              </select>
            </div>
          </div>

          {/* Results Info */}
          <div className="results-info">
            <p>عرض {indexOfFirstBook + 1}-{Math.min(indexOfLastBook, filteredBooks.length)} من {filteredBooks.length} كتاب</p>
          </div>

          {/* Books Grid */}
          <div className="books-grid">
            {currentBooks.map((book) => (
              <div key={book.id} className="book-card">
                <div className="book-image">
                  <i className="fas fa-book"></i>
                  <div className="book-rating">
                    <i className="fas fa-star"></i>
                    <span>{book.rating}</span>
                  </div>
                </div>
                <div className="book-info">
                  <span className="book-category">{book.category}</span>
                  <h3 className="book-title">{book.title}</h3>
                  <p className="book-author">بقلم: {book.author}</p>
                  <p className="book-description">{book.description}</p>
                  <div className="book-meta">
                    <span className="book-pages">{book.pages} صفحة</span>
                    <span className="book-language">{book.language}</span>
                  </div>
                  <div className="book-footer">
                    <span className="book-price">{book.price} جنيه</span>
                    <div className="book-actions">
                      <Link to={`/books/${book.id}`} className="btn btn-primary">
                        عرض التفاصيل
                      </Link>
                      <button 
                        className="btn btn-secondary"
                        onClick={() => addToCart(book)}
                      >
                        <i className="fas fa-shopping-cart"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-right"></i>
              </button>

              {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = index + 1;
                } else if (currentPage <= 3) {
                  pageNumber = index + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + index;
                } else {
                  pageNumber = currentPage - 2 + index;
                }

                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                  >
                    {pageNumber}
                  </button>
                );
              })}

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BooksPage;
